import type React from 'react'
import { type ReactNode, useCallback, useEffect, useMemo } from 'react'
import { useCollectionData } from 'react-firebase-hooks/firestore'

import { captureException } from '@sentry/react'
import { query, queryEqual, Timestamp, where } from 'firebase/firestore'
import { useSessionStorage } from 'usehooks-ts'

import { type DashboardAlert, dashboardAlertCollection } from '../firestore/dashboardAlertCollection.ts'

import { DashboardAlertContext } from './DashboardAlertContext.ts'

const timestampAtAppStartup = Timestamp.now()

export interface DashboardAlertProviderProps {
  regionId: string
  eveningId: number
  children: ReactNode
}

export const DashboardAlertProvider: React.FC<DashboardAlertProviderProps> = ({ regionId, eveningId, children }) => {
  const [dismissedAlertIds, setDismissedAlertIds] = useSessionStorage<string[]>(
    `dismissedAlerts_${regionId}_${eveningId}`,
    []
  )

  const alertsQuery = useMemo(
    () =>
      query(
        dashboardAlertCollection(),
        where('regionId', '==', regionId),
        where('eveningId', '==', eveningId),
        where('timestamp', '>=', timestampAtAppStartup)
      ),
    [regionId, eveningId]
  )
  const [snapshotData = [], loading, error, snapshot] = useCollectionData<DashboardAlert>(
    alertsQuery,
    // Without metadata changes, a result that is the same in the cache and on the server never
    // reports leaving the cache, and `synced` would stay false: no operations sound would ever play.
    { snapshotListenOptions: { includeMetadataChanges: true } }
  )
  const synced = snapshot !== undefined && !snapshot.metadata.fromCache && queryEqual(snapshot.query, alertsQuery)
  useEffect(() => {
    if (error) {
      captureException(error, {
        tags: { 'firestore.query.snapshot': 'dashboardAlerts' },
        fingerprint: ['firestore', 'query', 'dashboardAlerts'],
      })
    }
  }, [error])

  const dismissAlert = useCallback(
    (id: string) => {
      setDismissedAlertIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
    },
    [setDismissedAlertIds]
  )

  const value = useMemo(
    () => ({
      alerts: snapshotData.map((alert) => ({ ...alert, isDismissed: dismissedAlertIds.includes(alert._id) })),
      loading,
      synced,
      dismissAlert,
    }),
    [snapshotData, loading, synced, dismissedAlertIds, dismissAlert]
  )

  return <DashboardAlertContext.Provider value={value}>{children}</DashboardAlertContext.Provider>
}
